/**
 * Countdown Timer Script
 * File: assets/countdown-timer.js
 */

class CountdownTimer {
  constructor(element) {
    try {
      this.element = element;
      this.showSeparators = element.dataset.showSeparators === 'true';
      
      // Parse date with iOS compatibility
      const dateString = element.dataset.endDate;
      if (!dateString) {
        console.error('No end date provided');
        return;
      }
      
      // Enhanced iOS Safari date parsing
      // Try separate date/time components first (most reliable)
      const dateOnly = element.dataset.endDateOnly;
      const timeOnly = element.dataset.endTimeOnly;
      
      if (dateOnly && timeOnly) {
        this.endDate = this.parseSeparateDateTime(dateOnly, timeOnly);
      } else {
        // Fallback to combined date string
        this.endDate = this.parseDate(dateString);
      }
      
      this.init();
    } catch (error) {
      console.error('CountdownTimer constructor error:', error);
    }
  }

  parseSeparateDateTime(dateStr, timeStr) {
    try {
      // Parse date: YYYY-MM-DD
      const dateComponents = dateStr.split('-');
      const year = parseInt(dateComponents[0], 10);
      const month = parseInt(dateComponents[1], 10) - 1; // Month is 0-indexed
      const day = parseInt(dateComponents[2], 10);
      
      // Parse time: HH:MM:SS
      const timeComponents = timeStr.split(':');
      const hours = parseInt(timeComponents[0], 10);
      const minutes = parseInt(timeComponents[1], 10);
      const seconds = parseInt(timeComponents[2], 10) || 0;
      
      // Create date using individual components (most reliable method for iOS)
      const date = new Date(year, month, day, hours, minutes, seconds);
      return date.getTime();
    } catch (error) {
      console.error('Error parsing separate date/time:', error);
      return new Date().getTime();
    }
  }

  parseDate(dateString) {
    try {
      // Remove extra whitespace and split date and time
      const cleanString = dateString.trim();
      const parts = cleanString.split(' ');
      
      if (parts.length >= 2) {
        const datePart = parts[0]; // YYYY-MM-DD
        const timePart = parts[1]; // HH:MM:SS
        
        // Split date components
        const dateComponents = datePart.split('-');
        const year = parseInt(dateComponents[0], 10);
        const month = parseInt(dateComponents[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(dateComponents[2], 10);
        
        // Split time components
        const timeComponents = timePart.split(':');
        const hours = parseInt(timeComponents[0], 10);
        const minutes = parseInt(timeComponents[1], 10);
        const seconds = parseInt(timeComponents[2], 10) || 0;
        
        // Create date using individual components (works on all devices)
        const date = new Date(year, month, day, hours, minutes, seconds);
        return date.getTime();
      }
      
      // Fallback methods if the format is different
      // Try ISO format with 'T' separator
      const isoString = cleanString.replace(' ', 'T');
      let timestamp = new Date(isoString).getTime();
      
      if (!isNaN(timestamp)) {
        return timestamp;
      }
      
      // Last fallback - direct parsing
      timestamp = new Date(cleanString).getTime();
      
      if (isNaN(timestamp)) {
        console.error('Unable to parse date:', dateString);
        return new Date().getTime(); // Return current time as fallback
      }
      
      return timestamp;
    } catch (error) {
      console.error('Date parsing error:', error);
      return new Date().getTime();
    }
  }

  init() {
    try {
      if (isNaN(this.endDate)) {
        console.error('Invalid end date for countdown timer');
        return;
      }
      
      this.updateCountdown();
      this.interval = setInterval(() => {
        try {
          this.updateCountdown();
        } catch (error) {
          console.error('Update countdown error:', error);
        }
      }, 1000);
    } catch (error) {
      console.error('Init error:', error);
    }
  }

  updateCountdown() {
    try {
      const now = new Date().getTime();
      const distance = this.endDate - now;

      if (distance < 0) {
        clearInterval(this.interval);
        this.displayExpired();
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.displayTime(days, hours, minutes, seconds);
    } catch (error) {
      console.error('Error updating countdown:', error);
    }
  }

  displayTime(days, hours, minutes, seconds) {
    try {
      const daysEl = this.element.querySelector('.countdown-days');
      const hoursEl = this.element.querySelector('.countdown-hours');
      const minutesEl = this.element.querySelector('.countdown-minutes');
      const secondsEl = this.element.querySelector('.countdown-seconds');

      if (daysEl) daysEl.textContent = this.padZero(days);
      if (hoursEl) hoursEl.textContent = this.padZero(hours);
      if (minutesEl) minutesEl.textContent = this.padZero(minutes);
      if (secondsEl) secondsEl.textContent = this.padZero(seconds);
    } catch (error) {
      console.error('Error displaying time:', error);
    }
  }

  displayExpired() {
    try {
      const displayEl = this.element.querySelector('.countdown-timer-display');
      if (displayEl) {
        displayEl.innerHTML = '<div class="countdown-expired" style="font-size: 24px; color: #FFFFFF;">Countdown Ended</div>';
      }
    } catch (error) {
      console.error('Error displaying expired message:', error);
    }
  }

  padZero(num) {
    return num < 10 ? '0' + num : num;
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

// Initialize countdown timers
(function() {
  'use strict';
  
  const timerInstances = [];
  let initAttempts = 0;
  const maxAttempts = 5;

  function initializeCountdowns() {
    try {
      const timers = document.querySelectorAll('.countdown-timer-display');
      
      if (timers.length === 0 && initAttempts < maxAttempts) {
        // Retry if timers not found (iOS homepage issue)
        initAttempts++;
        setTimeout(initializeCountdowns, 200);
        return;
      }
      
      timers.forEach(function(timer) {
        try {
          // Avoid double initialization
          if (!timer.dataset.initialized) {
            timer.dataset.initialized = 'true';
            const instance = new CountdownTimer(timer);
            if (instance) {
              timerInstances.push(instance);
            }
          }
        } catch (error) {
          console.error('Error initializing individual countdown:', error);
        }
      });
    } catch (error) {
      console.error('Error in initializeCountdowns:', error);
    }
  }

  // Multiple initialization strategies for iOS compatibility
  
  // Strategy 1: Standard DOM ready check
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCountdowns);
  } else {
    initializeCountdowns();
  }
  
  // Strategy 2: Backup initialization for iOS (after a brief delay)
  setTimeout(function() {
    initializeCountdowns();
  }, 300);
  
  // Strategy 3: Window load fallback for homepage conflicts
  window.addEventListener('load', function() {
    initializeCountdowns();
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', function() {
    try {
      timerInstances.forEach(function(instance) {
        if (instance && instance.destroy) {
          instance.destroy();
        }
      });
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  });

  // Handle Shopify theme editor updates
  if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      try {
        const timers = event.target.querySelectorAll('.countdown-timer-display');
        timers.forEach(function(timer) {
          if (!timer.dataset.initialized) {
            timer.dataset.initialized = 'true';
            const instance = new CountdownTimer(timer);
            if (instance) {
              timerInstances.push(instance);
            }
          }
        });
      } catch (error) {
        console.error('Error in section load:', error);
      }
    });
  }
})();

